import { getServerUrl, getToken } from './session';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
  }
}

export class OfflineError extends Error {
  constructor() {
    super('No connection to the server');
  }
}

/** Absolute URL for an API path, honoring a configured server (native apps). */
export function apiUrl(path: string): string {
  const base = getServerUrl();
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

export interface ApiOptions {
  method?: string;
  body?: unknown;
  /** send bytes as-is (application/octet-stream) */
  raw?: BodyInit;
  headers?: Record<string, string>;
  auth?: boolean;
  signal?: AbortSignal;
  /** return the Response instead of parsing JSON */
  response?: boolean;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (opts.auth !== false) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  let body: BodyInit | undefined;
  if (opts.raw !== undefined) {
    body = opts.raw;
    headers['Content-Type'] ??= 'application/octet-stream';
  } else if (opts.body !== undefined) {
    body = JSON.stringify(opts.body);
    headers['Content-Type'] = 'application/json';
  }
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method: opts.method ?? (body ? 'POST' : 'GET'),
      headers,
      body,
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new OfflineError();
  }
  if (!res.ok) {
    let msg = res.statusText;
    let code: string | undefined;
    try {
      const j = (await res.json()) as { error?: string; code?: string };
      msg = j.error ?? msg;
      code = j.code;
    } catch {}
    throw new ApiError(res.status, msg, code);
  }
  if (opts.response) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  if (type.includes('application/json')) return (await res.json()) as T;
  return (await res.arrayBuffer()) as unknown as T;
}

export function isOfflineError(err: unknown): boolean {
  return err instanceof OfflineError || (err instanceof ApiError && err.status >= 502);
}
